import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Loader2, Plus, Trash2, Package } from "lucide-react";
import { useLang } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { TICKET_CATEGORY, TICKET_PRIORITY, TICKET_GROUPS, TICKET_TYPES } from "@/lib/tickets";

const SHIP_STATUS = [
  { id: "offen", de: "Offen", en: "Open" },
  { id: "versendet", de: "Versendet", en: "Shipped" },
  { id: "zugestellt", de: "Zugestellt", en: "Delivered" },
  { id: "erhalten", de: "Erhalten", en: "Received" },
];

export default function TicketDialog({ open, onClose, onSaved, initial, lockLocation, locations = [], devicesByLoc = {}, users = [] }) {
  const { lang } = useLang();
  const de = lang === "de";
  const editing = !!(initial && initial.id);
  const [locationId, setLocationId] = useState("");
  const [title, setTitle] = useState("");
  const [ticketType, setTicketType] = useState("versand");
  const [note, setNote] = useState("");
  const [category, setCategory] = useState("sonstiges");
  const [priority, setPriority] = useState("normal");
  const [dueDate, setDueDate] = useState("");
  const [deviceCode, setDeviceCode] = useState("");
  const [assignee, setAssignee] = useState("");
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [selProd, setSelProd] = useState("");
  const [selQty, setSelQty] = useState(1);
  const [shippingStatus, setShippingStatus] = useState("offen");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [titles, setTitles] = useState([]);
  const [titleMode, setTitleMode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/products").then((r) => setProducts(r.data || [])).catch(() => {});
    api.get("/ticket-titles").then((r) => setTitles(r.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t0 = initial?.title || "";
    if (!t0) { setTitleMode(""); return; }
    const m = (titles || []).find((x) => x.label === t0);
    setTitleMode(m ? m.id : "custom");
  }, [open, initial, titles]);

  useEffect(() => {
    if (!open) return;
    setLocationId(initial?.location_id || lockLocation || "");
    setTitle(initial?.title || "");
    setTicketType(initial?.ticket_type || "versand");
    setNote(initial?.note || "");
    setCategory(initial?.category || "sonstiges");
    setPriority(initial?.priority || "normal");
    setDueDate(initial?.due_date || "");
    setDeviceCode(initial?.device_code || "");
    setAssignee(initial ? (initial.assignee_type === "group" ? `g:${initial.assignee}` : initial.assignee ? `u:${initial.assignee}` : "") : "");
    setItems(initial?.items || []);
    setShippingStatus(initial?.shipping_status || "offen");
    setTrackingNumber(initial?.tracking_number || "");
    setSelProd(""); setSelQty(1);
  }, [open, initial, lockLocation]);

  if (!open) return null;

  const devices = devicesByLoc[locationId] || [];
  const activeTitles = (titles || []).filter((t) => t.active);
  const selTitle = activeTitles.find((t) => t.id === titleMode);
  const showCustomTitle = titleMode === "custom" || (selTitle && selTitle.label === "Sonstiges");
  const onTitleChoice = (v) => {
    setTitleMode(v);
    if (v === "custom") { setTitle(""); return; }
    const t = activeTitles.find((x) => x.id === v);
    if (t) {
      if (!editing) { if (t.category) setCategory(t.category); if (t.ticket_type) setTicketType(t.ticket_type); }
      setTitle(t.label === "Sonstiges" ? "" : t.label);
    } else setTitle("");
  };

  const save = async () => {
    if (!locationId.trim()) { toast.error(de ? "Standort erforderlich" : "Location required"); return; }
    if (!title.trim()) { toast.error(de ? "Titel erforderlich" : "Title required"); return; }
    let assignee_type = "", assigneeVal = "";
    if (assignee.startsWith("g:")) { assignee_type = "group"; assigneeVal = assignee.slice(2); }
    else if (assignee.startsWith("u:")) { assignee_type = "user"; assigneeVal = assignee.slice(2); }
    const payload = { location_id: locationId.trim(), title: title.trim(), note: note.trim(), ticket_type: ticketType, category, priority, due_date: dueDate, device_code: deviceCode, assignee: assigneeVal, assignee_type, items, shipping_status: shippingStatus, tracking_number: trackingNumber };
    setSaving(true);
    try {
      if (editing) await api.patch(`/tickets/${initial.id}`, payload);
      else await api.post("/tickets", payload);
      toast.success(editing ? (de ? "Ticket aktualisiert" : "Ticket updated") : (de ? "Ticket erstellt" : "Ticket created"));
      window.dispatchEvent(new Event("tickets:changed"));
      onSaved && onSaved();
      onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:border-amber-500/50";
  const labelCls = "block text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-1.5";

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="ticket-dialog" className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100vh-4rem)] overflow-y-auto bg-gray-50 border border-gray-200 rounded-2xl shadow-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">{editing ? (de ? "Ticket bearbeiten" : "Edit ticket") : (de ? "Neues Ticket" : "New ticket")}</h3>
          <button data-testid="ticket-dialog-close" onClick={onClose} className="text-gray-600 hover:text-gray-900"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className={labelCls}>{de ? "Standort" : "Location"}</label>
            {lockLocation || (editing) ? (
              <div className="font-mono text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{locationId}</div>
            ) : (
              <>
                <input data-testid="ticket-location-input" list="ticket-loc-list" value={locationId} onChange={(e) => { setLocationId(e.target.value.toUpperCase()); setDeviceCode(""); }} placeholder={de ? "Standortcode (z. B. MUCT01)" : "Location code"} className={inputCls} />
                <datalist id="ticket-loc-list">
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.name ? `${l.id} — ${l.name}` : l.id}</option>)}
                </datalist>
              </>
            )}
          </div>

          <div>
            <label className={labelCls}>{de ? "Titel" : "Title"}</label>
            <select data-testid="ticket-title-select" value={titleMode} onChange={(e) => onTitleChoice(e.target.value)} className={inputCls}>
              <option value="" className="bg-gray-50">{de ? "— Titel wählen —" : "— choose title —"}</option>
              {activeTitles.map((t) => <option key={t.id} value={t.id} className="bg-gray-50">{t.label}</option>)}
              <option value="custom" className="bg-gray-50">{de ? "✏️ Eigener Titel …" : "✏️ Custom title …"}</option>
            </select>
            {showCustomTitle && (
              <input data-testid="ticket-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={de ? "Eigenen Titel eingeben" : "Enter custom title"} className={`${inputCls} mt-2`} />
            )}
          </div>

          <div>
            <label className={labelCls}>{de ? "Ticket-Typ" : "Ticket type"}</label>
            <div className="grid grid-cols-3 gap-1.5" data-testid="ticket-type-group">
              {TICKET_TYPES.filter((tt) => tt.id !== "standort").map((tt) => (
                <button key={tt.id} type="button" data-testid={`ticket-type-${tt.id}`} onClick={() => setTicketType(tt.id)}
                  className={`text-xs font-medium rounded-lg px-2 py-2 border transition-colors ${ticketType === tt.id ? tt.cls : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"}`}>
                  {de ? tt.de : tt.en}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>{de ? "Notiz / Details" : "Note / details"}</label>
            <textarea data-testid="ticket-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder={de ? "optional" : "optional"} className={`${inputCls} resize-none`} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{de ? "Kategorie" : "Category"}</label>
              <select data-testid="ticket-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
                {TICKET_CATEGORY.map((c) => <option key={c.id} value={c.id} className="bg-gray-50">{de ? c.de : c.en}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{de ? "Priorität" : "Priority"}</label>
              <select data-testid="ticket-priority-select" value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>
                {TICKET_PRIORITY.map((p) => <option key={p.id} value={p.id} className="bg-gray-50">{de ? p.de : p.en}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{de ? "Fällig am" : "Due date"}</label>
              <input data-testid="ticket-due-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{de ? "Gerät (optional)" : "Device (optional)"}</label>
              <select data-testid="ticket-device-select" value={deviceCode} onChange={(e) => setDeviceCode(e.target.value)} className={inputCls} disabled={!locationId}>
                <option value="" className="bg-gray-50">{de ? "— keins —" : "— none —"}</option>
                {devices.map((d) => <option key={d.device_code} value={d.device_code} className="bg-gray-50">{d.device_code}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>{de ? "Zuständig" : "Assignee"}</label>
            <select data-testid="ticket-assignee-select" value={assignee} onChange={(e) => setAssignee(e.target.value)} className={inputCls}>
              <option value="" className="bg-gray-50">{de ? "Nicht zugewiesen" : "Unassigned"}</option>
              <optgroup label={de ? "Gruppen" : "Groups"}>
                {TICKET_GROUPS.map((g) => <option key={g.id} value={`g:${g.id}`} className="bg-gray-50">{de ? g.de : g.en}</option>)}
              </optgroup>
              {users.length > 0 && (
                <optgroup label={de ? "Benutzer" : "Users"}>
                  {users.map((u) => <option key={u.email} value={`u:${u.email}`} className="bg-gray-50">{u.name || u.email}</option>)}
                </optgroup>
              )}
            </select>
          </div>
        </div>

        {ticketType !== "remote" && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-gray-600 mb-2"><Package className="w-3.5 h-3.5" /> {ticketType === "retoure" ? (de ? "Produkte / Rückversand" : "Products / return") : (de ? "Produkte / Versand" : "Products / shipping")}</label>
          <div className="space-y-1.5 mb-2" data-testid="ticket-items-list">
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm">
                <span className="text-gray-900"><span className="font-mono text-amber-700">{it.qty}×</span> {it.name}</span>
                <button data-testid={`ticket-item-remove-${i}`} onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-gray-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="ticket-item-product" value={selProd} onChange={(e) => setSelProd(e.target.value)} className={`${inputCls} flex-1`}>
              <option value="" className="bg-gray-50">{de ? "Produkt wählen…" : "Choose product…"}</option>
              {products.map((p) => { const free = p.free ?? p.stock ?? 0; const avail = p.available ?? p.stock ?? 0; return <option key={p.id} value={p.id} className="bg-gray-50">{p.name} — {de ? "frei" : "free"} {free}{free !== avail ? ` / ${avail}` : ""}</option>; })}
            </select>
            <input data-testid="ticket-item-qty" type="number" min="1" value={selQty} onChange={(e) => setSelQty(e.target.value)} className={`${inputCls} w-16`} />
            <button data-testid="ticket-item-add" type="button" onClick={() => { const p = products.find((x) => x.id === selProd); const q = parseInt(selQty) || 0; if (p && q > 0) { const free = p.free ?? p.stock ?? 0; if (q > free) toast(de ? `Hinweis: nur ${free} verfügbar – Nachbestellung nötig.` : `Note: only ${free} available.`); setItems([...items, { product_id: p.id, name: p.name, qty: q }]); setSelProd(""); setSelQty(1); } }} className="shrink-0 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900"><Plus className="w-4 h-4" /></button>
          </div>
          {selProd && (() => { const p = products.find((x) => x.id === selProd); if (!p) return null; const free = p.free ?? p.stock ?? 0; const over = (parseInt(selQty) || 0) > free; return <p data-testid="ticket-item-availability" className={`text-[11px] mt-1.5 ${over ? "text-amber-700" : "text-gray-600"}`}>{de ? `Verfügbar: ${free}${p.track_assets ? " einsatzbereit im Lager" : ""}${over ? " — Menge übersteigt Verfügbarkeit (Nachbestellung nötig)" : ""}` : `Available: ${free}${over ? " — exceeds availability" : ""}`}</p>; })()}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className={labelCls}>{de ? "Versandstatus" : "Shipping status"}</label>
              <select data-testid="ticket-shipping-status" value={shippingStatus} onChange={(e) => setShippingStatus(e.target.value)} className={inputCls}>
                {SHIP_STATUS.map((s) => <option key={s.id} value={s.id} className="bg-gray-50">{de ? s.de : s.en}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{de ? "Tracking-Nr." : "Tracking no."}</label>
              <input data-testid="ticket-tracking-input" value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className={inputCls} placeholder={de ? "z. B. DHL …" : "e.g. DHL …"} />
            </div>
          </div>
          {shippingStatus !== "offen" && items.length > 0 && <p className="text-[11px] text-amber-700/80 mt-2">{de ? "Hinweis: Der Bestand wird bei Versand automatisch abgebucht." : "Note: stock is deducted automatically on shipping."}</p>}
        </div>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <button data-testid="ticket-cancel-btn" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors">{de ? "Abbrechen" : "Cancel"}</button>
          <button data-testid="ticket-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-500/90 hover:bg-amber-500 text-black transition-colors disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? (de ? "Speichern" : "Save") : (de ? "Anlegen" : "Create")}
          </button>
        </div>
      </div>
    </>
  );
}
